import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  alpha
} from "@mui/material";
import { format } from "date-fns";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { fNumber } from "../utils/formatNumber";

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return format(d, "dd-MM-yyyy");
};

const fmtFriendly = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return format(d, "PP p");
};

const fmtCurrency = (value) => {
  if (value == null || value === "") return "—";
  const clean = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(clean)) return String(value);
  return `₦${fNumber(clean)}`;
};

const fmtUser = (u) => {
  if (!u) return "System";
  if (typeof u === "string") return u;
  if (typeof u === "object") {
    return u.username ?? u.name ?? u.fullName ?? u._id ?? "System";
  }
  return String(u);
};

const renderTxTypeChip = (rawVal, accountType) => {
  const raw = String(rawVal || "").toLowerCase().trim();

  let label = rawVal;
  let bg = "#F1F5F9";
  let color = "#475569";
  let border = "#CBD5E1";

  if (accountType === "business") {
    if (raw === "credit" || raw === "buy") {
      label = "Buy Financing";
      bg = "#F0F9FF";
      color = "#0369A1";
      border = "#BAE6FD";
    } else if (raw === "debit" || raw === "repayment") {
      label = "Repayment";
      bg = "#ECFDF5";
      color = "#047857";
      border = "#A7F3D0";
    }
  } else if (accountType === "loan") {
    if (raw === "credit" || raw === "approved loan" || raw === "get loan") {
      label = "Approved Loan";
      bg = "#F0F9FF";
      color = "#0369A1";
      border = "#BAE6FD";
    } else if (raw === "debit" || raw === "loan repayment" || raw === "repay loan") {
      label = "Loan Repayment";
      bg = "#ECFDF5";
      color = "#047857";
      border = "#A7F3D0";
    }
  } else {
    if (raw === "credit" || raw === "deposit") {
      label = "Credit";
      bg = "#ECFDF5";
      color = "#047857";
      border = "#A7F3D0";
    } else if (raw === "debit" || raw === "withdrawal") {
      label = "Debit";
      bg = "#FFF1F2";
      color = "#B91C1C";
      border = "#FECDD3";
    }
  }

  return (
    <Chip
      label={label || "—"}
      size="small"
      sx={{
        height: 22,
        fontSize: "0.72rem",
        fontWeight: 700,
        backgroundColor: bg,
        color: color,
        border: `1px solid ${border}`
      }}
    />
  );
};

export default function TransactionTable({
  transactions = [],
  columns = [],
  pageSizeOptions = [5, 10, 20, 50],
  initialPageSize = 10,
  onView,
  onEdit,
  onDelete,
  showBack = false,
  onBack,
  accountType
}) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const hasActions = Boolean(onView || onEdit || onDelete);

  const defaultCols = useMemo(() => [
    { key: "date", label: "Date", align: "left", render: (r) => (
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem" }}>
        {fmtDate(r.date)}
      </Typography>
    )},
    {
      key: "amount",
      label: "Amount",
      align: "right",
      render: (r) => {
        const rawType = String(r.type || r.txType || r.transactionType || r.creditOrDebit || "").toLowerCase();
        const isCredit = rawType.includes("credit") || rawType.includes("buy") || rawType.includes("deposit");
        return (
          <Typography
            variant="body2"
            sx={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: 700,
              fontSize: "0.84rem",
              color: isCredit ? "#047857" : "#B91C1C"
            }}
          >
            {fmtCurrency(r.amount)}
          </Typography>
        );
      }
    },
    {
      key: "type",
      label: "Type",
      align: "left",
      render: (r) =>
        renderTxTypeChip(
          r.type || r.txType || r.transactionType || r.creditOrDebit || "",
          accountType
        )
    },
    ...(accountType === "business" || accountType === "loan"
      ? [
          {
            key: "tenor",
            label: "Tenor",
            align: "center",
            render: (r) => {
              const raw = String(
                r.type || r.txType || r.transactionType || r.creditOrDebit || ""
              ).toLowerCase().trim();
              if (accountType === "business") {
                if (raw === "credit" || raw === "buy") {
                  return r.tenor ? (
                    <Chip
                      label={`${r.tenor}M`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                    />
                  ) : "—";
                }
                return "—";
              }
              if (accountType === "loan") {
                if (raw === "credit" || raw === "approved loan" || raw === "get loan") {
                  const m = r.tenorMonths || r.tenor;
                  return m ? (
                    <Chip
                      label={`${m}M`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                    />
                  ) : "—";
                }
                return "—";
              }
              return "—";
            }
          }
        ]
      : []),
    {
      key: "balance",
      label: "New Balance",
      align: "right",
      render: (r) => (
        <Typography
          variant="body2"
          sx={{
            fontVariantNumeric: "tabular-nums",
            fontWeight: 600,
            fontSize: "0.84rem",
            color: "text.primary"
          }}
        >
          {fmtCurrency(r.balance ?? r.newBalance ?? r.amount)}
        </Typography>
      )
    },
    {
      key: "createdBy",
      label: "Posted By",
      align: "left",
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem", color: "text.primary" }}>
            {fmtUser(r.createdBy)}
          </Typography>
          {r.createdAt ? (
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
              {fmtFriendly(r.createdAt)}
            </Typography>
          ) : null}
        </Box>
      )
    },
    {
      key: "description",
      label: "Description",
      align: "left",
      render: (r) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.8rem",
            color: "text.secondary",
            maxWidth: 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
          title={r.description || ""}
        >
          {r.description || "—"}
        </Typography>
      )
    }
  ], [accountType]);

  const cols = columns && columns.length ? columns : defaultCols;

  const filteredTransactions = useMemo(() => {
    const rows = transactions || [];
    const q = (query || "").toString().trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      for (const c of cols) {
        try {
          const v = typeof c.render === "function" ? c.render(r) : (r[c.key] ?? "");
          let txt = "";
          if (v == null) txt = "";
          else if (typeof v === "string" || typeof v === "number") txt = String(v);
          else if (typeof v === "object" && v.props && v.props.children) {
            const ch = v.props.children;
            if (typeof ch === "string" || typeof ch === "number") txt = String(ch);
            else if (Array.isArray(ch))
              txt = ch.map((x) => (typeof x === "string" ? x : (x?.props?.children ?? ""))).join(" ");
            else txt = String(ch?.props?.children ?? "");
          } else {
            txt = String(v);
          }
          if (txt.toLowerCase().includes(q)) return true;
        } catch {
          const fallback = String(r[c.key] ?? "");
          if (fallback.toLowerCase().includes(q)) return true;
        }
      }
      const raw = Object.values(r).join(" ").toLowerCase();
      return raw.includes(q);
    });
  }, [transactions, query, cols]);

  const sorted = useMemo(() => {
    const source = filteredTransactions;
    if (!sortKey) return [...source];
    const arr = [...source].sort((a, b) => {
      const va = typeof a[sortKey] !== "undefined" ? a[sortKey] : (a?.[sortKey] ?? "");
      const vb = typeof b[sortKey] !== "undefined" ? b[sortKey] : (b?.[sortKey] ?? "");
      if (va === vb) return 0;
      const na = Number(va);
      const nb = Number(vb);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) {
        return (na - nb) * (sortDir === "asc" ? 1 : -1);
      }
      return String(va).localeCompare(String(vb)) * (sortDir === "asc" ? 1 : -1);
    });
    return arr;
  }, [filteredTransactions, sortKey, sortDir]);

  const visible = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
      return;
    }
    if (sortDir === "asc") setSortDir("desc");
    else if (sortDir === "desc") {
      setSortKey(null);
      setSortDir(null);
    } else {
      setSortDir("asc");
    }
  };

  const handleConfirmDelete = () => {
    if (deleteTarget && onDelete) {
      onDelete(deleteTarget);
    }
    setDeleteTarget(null);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        backgroundColor: "background.paper",
        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.04)"
      }}
    >
      {/* Modern Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          p: { xs: 1.5, sm: 2 },
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#FAFCFE"
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          {showBack && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 18 }} />}
              onClick={() => {
                if (typeof onBack === "function") return onBack();
                if (typeof window !== "undefined" && window.history.length > 1)
                  return window.history.back();
                window.location.href = "/";
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                borderRadius: 2,
                borderColor: "divider",
                color: "text.primary",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover"
                }
              }}
            >
              Back
            </Button>
          )}

          <TextField
            size="small"
            placeholder="Search transactions..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(0);
            }}
            sx={{
              width: { xs: "100%", sm: 300 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.paper",
                fontSize: "0.875rem",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "primary.main"
                },
                "&.Mui-focused": {
                  boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setQuery("");
                      setPage(0);
                    }}
                    edge="end"
                    aria-label="clear search"
                  >
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            size="small"
            variant="outlined"
            label={
              query
                ? `${filteredTransactions.length} of ${transactions.length} records`
                : `${transactions.length} transactions`
            }
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              borderColor: "divider",
              backgroundColor: "background.paper",
              color: "text.secondary"
            }}
          />
        </Box>
      </Box>

      {/* Modern Table Container */}
      <TableContainer
        sx={{
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": {
            height: 6,
            width: 6
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.12)",
            borderRadius: 3
          }
        }}
      >
        <Table size="small" sx={{ minWidth: { xs: 720, md: "100%" } }}>
          <TableHead>
            <TableRow
              sx={(theme) => ({
                backgroundColor:
                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#F8FAFC",
                borderBottom: `2px solid ${theme.palette.divider}`
              })}
            >
              {cols.map((c) => {
                const isAmountCol = c.align === "right" || /(amount|balance|newbalance)/i.test(c.key);
                return (
                  <TableCell
                    key={c.key}
                    align={c.align || (isAmountCol ? "right" : "left")}
                    onClick={() => handleSort(c.key)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: sortKey === c.key ? "primary.main" : "text.secondary",
                      cursor: "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        color: "primary.main"
                      }
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent={
                        c.align === "right"
                          ? "flex-end"
                          : c.align === "center"
                          ? "center"
                          : "flex-start"
                      }
                      gap={0.5}
                    >
                      <span>{c.label}</span>
                      {sortKey === c.key ? (
                        sortDir === "desc" ? (
                          <ArrowDownwardRoundedIcon sx={{ fontSize: 16, color: "primary.main" }} />
                        ) : (
                          <ArrowUpwardRoundedIcon sx={{ fontSize: 16, color: "primary.main" }} />
                        )
                      ) : (
                        <UnfoldMoreRoundedIcon
                          sx={{ fontSize: 16, color: "text.disabled", opacity: 0.4 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                );
              })}
              {hasActions && (
                <TableCell
                  align="right"
                  sx={{
                    py: 1.5,
                    px: 2,
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "text.secondary",
                    width: 110
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={cols.length + (hasActions ? 1 : 0)}
                  align="center"
                  sx={{ py: 6, borderBottom: "none" }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                  >
                    <SearchOffRoundedIcon sx={{ fontSize: 42, color: "text.disabled", opacity: 0.6 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      No transactions found
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.disabled" }}>
                      {query ? "Try adjusting your search criteria" : "No records recorded yet"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              visible.map((row, idx) => {
                const txType = String(
                  row.type || row.txType || row.transactionType || row.creditOrDebit || ""
                ).toLowerCase().trim();
                const isCredit =
                  txType.includes("credit") ||
                  txType.includes("buy") ||
                  txType.includes("deposit") ||
                  txType.includes("approved loan");
                const isDebit =
                  txType.includes("debit") ||
                  txType.includes("withdrawal") ||
                  txType.includes("repayment");

                return (
                  <TableRow
                    key={row.id || row._id || idx}
                    sx={(theme) => ({
                      transition: "background-color 140ms ease-in-out",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.035)
                      },
                      borderLeft: `3px solid ${
                        isCredit ? "#10B981" : isDebit ? "#EF4444" : "transparent"
                      }`,
                      "& td": {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        py: 1.25,
                        px: 2,
                        fontSize: "0.84rem",
                        color: "text.primary"
                      }
                    })}
                  >
                    {cols.map((c) => {
                      const isAmountCol = c.align === "right" || /(amount|balance|newbalance)/i.test(c.key);
                      return (
                        <TableCell
                          key={c.key}
                          align={c.align || (isAmountCol ? "right" : "left")}
                          sx={{ whiteSpace: isAmountCol ? "nowrap" : "normal" }}
                        >
                          {typeof c.render === "function" ? c.render(row) : (row[c.key] ?? "—")}
                        </TableCell>
                      );
                    })}
                    {hasActions && (
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          {onView && (
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => onView(row)}
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: "primary.main",
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                  }
                                }}
                              >
                                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onEdit && (
                            <Tooltip title="Edit Record">
                              <IconButton
                                size="small"
                                onClick={() => onEdit(row)}
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: "info.main",
                                    backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08)
                                  }
                                }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onDelete && (
                            <Tooltip title="Delete Record">
                              <IconButton
                                size="small"
                                onClick={() => setDeleteTarget(row)}
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: "error.main",
                                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08)
                                  }
                                }}
                              >
                                <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modern Table Footer / Pagination */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          p: { xs: 1, sm: 1.5 },
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.01)" : "#FAFCFE"
        }}
      >
        <TablePagination
          component="div"
          rowsPerPageOptions={pageSizeOptions}
          count={filteredTransactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          sx={{
            border: "none",
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
              fontSize: "0.8rem",
              color: "text.secondary"
            },
            ".MuiTablePagination-toolbar": {
              minHeight: 44,
              px: 1
            }
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            maxWidth: 440
          }
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "#FFF1F2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#EF4444"
            }}
          >
            <WarningAmberRoundedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
            Confirm Record Deletion
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <DialogContentText sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
            Are you sure you want to delete this transaction record (Amount:{" "}
            <strong>{deleteTarget ? fmtCurrency(deleteTarget.amount) : "—"}</strong>)?
            This action cannot be undone and will alter the member ledger history.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteTarget(null)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              borderColor: "divider"
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" }
            }}
          >
            Delete Record
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}